import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Battery, Target, TrendingUp, ChevronDown, ChevronUp, Star, Award, Clock, Swords, Trophy, Shield, Flame, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiBaseUrl } from '../utils/apiUrl';

const PokemonMovesSection = ({ pokemonId, pokemonName }) => {
  const { t, language } = useLanguage();
  const [movesData, setMovesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllMoves, setShowAllMoves] = useState(false);

  const getTypeColor = (type) => {
    const colors = {
      'Normal': 'bg-gray-400',
      'Fire': 'bg-red-500',
      'Water': 'bg-blue-500',
      'Electric': 'bg-yellow-500',
      'Grass': 'bg-green-500',
      'Ice': 'bg-cyan-400',
      'Fighting': 'bg-orange-600',
      'Poison': 'bg-purple-500',
      'Ground': 'bg-amber-600',
      'Flying': 'bg-indigo-400',
      'Psychic': 'bg-pink-500',
      'Bug': 'bg-lime-500',
      'Rock': 'bg-yellow-700',
      'Ghost': 'bg-purple-700',
      'Dragon': 'bg-indigo-600',
      'Dark': 'bg-gray-700',
      'Steel': 'bg-gray-500',
      'Fairy': 'bg-pink-400'
    };
    return colors[type] || 'bg-gray-400';
  };

  const getTypeGradient = (type) => {
    const gradients = {
      'Normal': 'from-gray-400 to-gray-500',
      'Fire': 'from-red-400 to-orange-500',
      'Water': 'from-blue-400 to-cyan-500',
      'Electric': 'from-yellow-400 to-amber-500',
      'Grass': 'from-green-400 to-emerald-500',
      'Ice': 'from-cyan-300 to-blue-400',
      'Fighting': 'from-orange-500 to-red-600',
      'Poison': 'from-purple-400 to-violet-600',
      'Ground': 'from-amber-500 to-yellow-700',
      'Flying': 'from-indigo-300 to-blue-500',
      'Psychic': 'from-pink-400 to-purple-500',
      'Bug': 'from-lime-400 to-green-600',
      'Rock': 'from-yellow-600 to-amber-700',
      'Ghost': 'from-purple-600 to-indigo-800',
      'Dragon': 'from-indigo-500 to-purple-700',
      'Dark': 'from-gray-600 to-gray-800',
      'Steel': 'from-gray-400 to-slate-600',
      'Fairy': 'from-pink-300 to-rose-500'
    };
    return gradients[type] || 'from-gray-400 to-gray-500';
  };

  // Get tier color class based on tier letter
  const getTierColor = (tier) => {
    if (!tier) return 'text-white';
    const tierColors = {
      'S': 'text-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]',
      'A+': 'text-orange-300 drop-shadow-[0_0_6px_rgba(253,186,116,0.7)]',
      'A': 'text-orange-200',
      'B': 'text-green-300',
      'C': 'text-cyan-200',
      'D': 'text-gray-300',
      'F': 'text-gray-400'
    };
    return tierColors[tier] || 'text-white';
  };

  const fetchMoves = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${getApiBaseUrl()}/api/moves/pokemon/${pokemonId}`
      );
      const data = await response.json();

      if (data.success) {
        setMovesData(data.data);
      } else {
        setError(data.error || t('common.noData'));
      }
    } catch (err) {
      console.error('Error fetching moves:', err);
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [pokemonId]);

  useEffect(() => {
    fetchMoves();
  }, [fetchMoves]);

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 shadow-pokemon">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-200 rounded-xl"></div>
            <div className="h-16 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !movesData) {
    return null; // Hide component if no moves data
  }

  const { bestMove, otherMoves, movePool } = movesData;

  // Get unique fast and charged moves
  const uniqueFastMoves = movePool?.fastMoves?.filter((move, index, self) => 
    move.name_en && index === self.findIndex(m => m.name_en === move.name_en)
  ) || [];
  
  const uniqueChargedMoves = movePool?.chargedMoves?.filter((move, index, self) => 
    move.name_en && index === self.findIndex(m => m.name_en === move.name_en)
  ) || [];

  const MoveCard = ({ move, type, isChargedMove = false, isBest = false }) => {
    if (!move?.name_en) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative p-4 rounded-xl bg-gradient-to-r ${getTypeGradient(move.type)} shadow-md overflow-hidden`}
      >
        {isBest && (
          <div className="absolute top-2 right-2">
            <div className="bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
              <Star className="w-3 h-3" /> Best
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {isChargedMove ? (
                <Battery className="w-5 h-5 text-white" />
              ) : (
                <Zap className="w-5 h-5 text-white" />
              )}
              <span className="text-white font-bold text-lg">{move.name_en}</span>
            </div>
            <div className="text-white/80 text-sm">{move.name_th || move.name_en}</div>
            
            <div className="flex items-center gap-3 mt-2 text-white/90 text-xs">
              <span className={`px-2 py-0.5 rounded-full ${getTypeColor(move.type)} text-white`}>
                {move.type}
              </span>
              {move.damage && (
                <span className="flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  {move.damage} DMG
                </span>
              )}
              {move.energy && (
                <span className="flex items-center gap-1">
                  <Battery className="w-3 h-3" />
                  {isChargedMove ? `-${move.energy}` : `+${move.energy}`}
                </span>
              )}
            </div>
          </div>
          
          {(move.dpt || move.ept || move.dpe) && (
            <div className="text-right text-white/90 text-xs">
              {move.dpt && <div>DPT: {move.dpt.toFixed(1)}</div>}
              {move.ept && <div>EPT: {move.ept.toFixed(1)}</div>}
              {move.dpe && <div>DPE: {move.dpe.toFixed(2)}</div>}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="bg-white rounded-3xl p-6 shadow-pokemon"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
          <Zap className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">{t('moves.title')}</h2>
          <p className="text-sm text-gray-500">{t('moves.subtitle')}</p>
        </div>
      </div>

      {/* Best Moveset */}
      {bestMove && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-yellow-500" />
            <h3 className="text-lg font-semibold text-gray-700">{t('moves.best')}</h3>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>
          
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-4 border border-yellow-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <MoveCard move={bestMove.fastMove} isChargedMove={false} isBest={true} />
              <MoveCard move={bestMove.chargedMove} isChargedMove={true} isBest={true} />
            </div>
            
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-gray-600">DPS:</span>
                <span className="font-bold text-green-600">{bestMove.dps?.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <Target className="w-4 h-4 text-blue-500" />
                <span className="text-gray-600">TDO:</span>
                <span className="font-bold text-blue-600">{bestMove.tdo?.toFixed(0)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tier Rankings - Combat Roles */}
      {movesData.tierRankings && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-gray-700">
              {t('moves.tierRanking')}
            </h3>
          </div>
          
          {/* Raid Attacker & Gym Defender */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {movesData.tierRankings.raidAttacker && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-red-500 to-orange-600 rounded-xl p-3 sm:p-4 text-white shadow-lg"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm font-medium">
                    {t('moves.raidAttacker')}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl sm:text-3xl font-black ${getTierColor(movesData.tierRankings.raidAttacker.tier)}`}>
                    {movesData.tierRankings.raidAttacker.tier}
                  </span>
                  <span className="text-xs sm:text-sm text-white/80">
                    #{movesData.tierRankings.raidAttacker.rank}
                  </span>
                </div>
              </motion.div>
            )}
            
            {movesData.tierRankings.gymDefender && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-gradient-to-br from-blue-600 to-cyan-700 rounded-xl p-3 sm:p-4 text-white shadow-lg"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm font-medium">
                    {t('moves.gymDefender')}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl sm:text-3xl font-black ${getTierColor(movesData.tierRankings.gymDefender.tier)}`}>
                    {movesData.tierRankings.gymDefender.tier}
                  </span>
                  {movesData.tierRankings.gymDefender.rank && (
                    <span className="text-xs sm:text-sm text-white/80">
                      #{movesData.tierRankings.gymDefender.rank}
                    </span>
                  )}
                </div>
              </motion.div>
            )}
            
            {movesData.tierRankings.typeAttacker && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`bg-gradient-to-br ${getTypeGradient(movesData.tierRankings.typeAttacker.type)} rounded-xl p-3 sm:p-4 text-white shadow-lg`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Swords className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-xs sm:text-sm font-medium">
                    {movesData.tierRankings.typeAttacker.type} Attacker
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl sm:text-3xl font-black ${getTierColor(movesData.tierRankings.typeAttacker.tier)}`}>
                    {movesData.tierRankings.typeAttacker.tier}
                  </span>
                  <span className="text-xs sm:text-sm text-white/80">
                    #{movesData.tierRankings.typeAttacker.rank}
                  </span>
                </div>
                {movesData.tierRankings.typeAttacker.moves && (
                  <div className="mt-2 text-xs bg-black/20 rounded px-2 py-1 truncate">
                    {movesData.tierRankings.typeAttacker.moves}
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* PvP Tier Rankings */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {movesData.tierRankings.greatLeague && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 }}
                className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-3 sm:p-4 text-white shadow-md"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4" />
                    <span className="text-sm font-medium">Great League</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-xl sm:text-2xl font-black ${getTierColor(movesData.tierRankings.greatLeague.tier)}`}>
                      {movesData.tierRankings.greatLeague.tier}
                    </span>
                    <span className="text-xs text-white/70">#{movesData.tierRankings.greatLeague.rank}</span>
                  </div>
                </div>
                {movesData.tierRankings.greatLeague.moves && (
                  <div className="text-xs bg-black/20 rounded px-2 py-1.5 mt-1 leading-relaxed">
                    {movesData.tierRankings.greatLeague.moves}
                  </div>
                )}
              </motion.div>
            )}
            
            {movesData.tierRankings.ultraLeague && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl p-3 sm:p-4 text-white shadow-md"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4" />
                    <span className="text-sm font-medium">Ultra League</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-xl sm:text-2xl font-black ${getTierColor(movesData.tierRankings.ultraLeague.tier)}`}>
                      {movesData.tierRankings.ultraLeague.tier}
                    </span>
                    <span className="text-xs text-white/70">#{movesData.tierRankings.ultraLeague.rank}</span>
                  </div>
                </div>
                {movesData.tierRankings.ultraLeague.moves && (
                  <div className="text-xs bg-black/20 rounded px-2 py-1.5 mt-1 leading-relaxed">
                    {movesData.tierRankings.ultraLeague.moves}
                  </div>
                )}
              </motion.div>
            )}
            
            {movesData.tierRankings.masterLeague && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.25 }}
                className="bg-gradient-to-br from-purple-600 to-violet-700 rounded-xl p-3 sm:p-4 text-white shadow-md"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4" />
                    <span className="text-sm font-medium">Master League</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-xl sm:text-2xl font-black ${getTierColor(movesData.tierRankings.masterLeague.tier)}`}>
                      {movesData.tierRankings.masterLeague.tier}
                    </span>
                    <span className="text-xs text-white/70">#{movesData.tierRankings.masterLeague.rank}</span>
                  </div>
                </div>
                {movesData.tierRankings.masterLeague.moves && (
                  <div className="text-xs bg-black/20 rounded px-2 py-1.5 mt-1 leading-relaxed">
                    {movesData.tierRankings.masterLeague.moves}
                  </div>
                )}
              </motion.div>
            )}
          </div>
          
          <p className="text-xs text-gray-500 mt-3 text-center">
            {t('moves.tierExplain')}
          </p>
        </div>
      )}

      {/* Move Pool - Fast Moves */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-5 h-5 text-yellow-500" />
          <h3 className="text-lg font-semibold text-gray-700">{t('moves.fast')}</h3>
          <span className="text-sm text-gray-400">({uniqueFastMoves.length})</span>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {uniqueFastMoves.map((move, index) => (
            <motion.div
              key={move.name_en || index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`p-4 rounded-xl bg-gradient-to-r ${getTypeGradient(move.type)} text-white shadow-md`}
            >
              {/* Move Name Header with Background */}
              <div className="bg-black/20 backdrop-blur-sm rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 flex-shrink-0" />
                    <span className="font-bold text-base sm:text-lg leading-tight">{move.name_en}</span>
                  </div>
                  <span className="text-xs sm:text-sm bg-white/30 px-2 sm:px-3 py-1 rounded-full font-medium flex-shrink-0">{move.type}</span>
                </div>
                {move.name_th && (
                  <div className="text-sm sm:text-base text-white/95 mt-1 ml-7 font-medium">{move.name_th}</div>
                )}
              </div>
              
              {/* Stats Row */}
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap text-xs sm:text-sm">
                <div className="flex items-center gap-1 sm:gap-1.5 bg-white/25 px-2 sm:px-3 py-1.5 rounded-lg" title={t('moves.damage')}>
                  <Swords className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="font-bold">{move.damage || '-'}</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-1.5 bg-white/25 px-2 sm:px-3 py-1.5 rounded-lg" title={t('moves.energy')}>
                  <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="font-bold">+{move.energy || '-'}</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-1.5 bg-white/25 px-2 sm:px-3 py-1.5 rounded-lg" title={t('moves.duration')}>
                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="font-bold">{move.turns ? (move.turns * 0.5).toFixed(1) : '-'}s</span>
                </div>
              </div>
              
              {/* DPT/EPT Row */}
              {(move.dpt || move.ept) && (
                <div className="flex items-center gap-3 sm:gap-4 mt-2 sm:mt-3 text-xs sm:text-sm text-white/90 bg-black/10 rounded-lg px-3 py-2">
                  {move.dpt && <span className="font-semibold">DPT: {move.dpt.toFixed(1)}</span>}
                  {move.ept && <span className="font-semibold">EPT: {move.ept.toFixed(1)}</span>}
                  {move.turns && <span className="text-white/70">({move.turns} {t('moves.turns')})</span>}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Move Pool - Charged Moves */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Battery className="w-5 h-5 text-purple-500" />
          <h3 className="text-lg font-semibold text-gray-700">{t('moves.charged')}</h3>
          <span className="text-sm text-gray-400">({uniqueChargedMoves.length})</span>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {uniqueChargedMoves.map((move, index) => (
            <motion.div
              key={move.name_en || index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`p-4 rounded-xl bg-gradient-to-r ${getTypeGradient(move.type)} text-white shadow-md`}
            >
              {/* Move Name Header with Background */}
              <div className="bg-black/20 backdrop-blur-sm rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Battery className="w-5 h-5 flex-shrink-0" />
                    <span className="font-bold text-base sm:text-lg leading-tight">{move.name_en}</span>
                  </div>
                  <span className="text-xs sm:text-sm bg-white/30 px-2 sm:px-3 py-1 rounded-full font-medium flex-shrink-0">{move.type}</span>
                </div>
                {move.name_th && (
                  <div className="text-sm sm:text-base text-white/95 mt-1 ml-7 font-medium">{move.name_th}</div>
                )}
              </div>
              
              {/* Stats Row */}
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap text-xs sm:text-sm">
                <div className="flex items-center gap-1 sm:gap-1.5 bg-white/25 px-2 sm:px-3 py-1.5 rounded-lg" title={t('moves.damage')}>
                  <Swords className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="font-bold">{move.damage || '-'}</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-1.5 bg-white/25 px-2 sm:px-3 py-1.5 rounded-lg" title={t('moves.energy')}>
                  <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="font-bold">-{move.energy || '-'}</span>
                </div>
                {move.dpe && (
                  <div className="flex items-center gap-1 sm:gap-1.5 bg-white/25 px-2 sm:px-3 py-1.5 rounded-lg" title="Damage Per Energy">
                    <span className="font-bold">DPE: {move.dpe.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Other Move Combinations */}
      {otherMoves && otherMoves.length > 0 && (
        <div>
          <button
            onClick={() => setShowAllMoves(!showAllMoves)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-3"
          >
            {showAllMoves ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {t('moves.other')} ({otherMoves.length} {t('moves.combinations')})
          </button>
          
          <AnimatePresence>
            {showAllMoves && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  {otherMoves.slice(0, 10).map((combo, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
                    >
                      <div className="text-gray-400 font-mono text-sm w-6">#{index + 2}</div>
                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-1 rounded-full text-xs text-white ${getTypeColor(combo.fastMove?.type)}`}>
                          ⚡ {combo.fastMove?.name_en}
                        </span>
                        <span className="text-gray-400">+</span>
                        <span className={`px-2 py-1 rounded-full text-xs text-white ${getTypeColor(combo.chargedMove?.type)}`}>
                          🔋 {combo.chargedMove?.name_en}
                        </span>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>DPS: {combo.dps?.toFixed(2)}</div>
                        <div>TDO: {combo.tdo?.toFixed(0)}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100 text-center text-xs text-gray-400">
        {t('moves.tip')}
      </div>
    </motion.div>
  );
};

export default PokemonMovesSection;

